import React, { useEffect, useMemo } from 'react';
import PropType from 'prop-types';
import { withStyles, Grid } from '@material-ui/core';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import Button from 'components/buttons/Button';
import Select from 'components/forms/Select';
import { getAccounts } from 'redux/actions/accountActions';
import { getCampaigns } from 'redux/actions/campaignActions';
import { getAccountEmailConfigs } from 'redux/actions/emailConfigActions';
import { resultsToOptions } from 'utils/misc';
import moment from 'moment';
import { first } from 'lodash';
import { requiredOptionValidator } from 'utils/validators';
import RenderSelectInput from 'components/forms/RenderSelectInput';
import { timlineRunInfo } from 'themes/colors';
import DateTimeWithRangeFilter, { LAST_MONTH_DATE_OPTION } from './DateTimeWithRangeFilter';


const TimelineFilter = ({
  classes,
  handleSubmit,
  invalid,
  submitting,
  ...props
}) => {
  const loadAccounts = async (search) => {
    const accounts = await props.getAccounts({ search, status: true }, true);
    return resultsToOptions(accounts.results, 'name') || [];
  };

  const loadCampaigns = async (search) => {
    const campaigns = await props.getCampaigns({
      search,
      account__id: props.selectedAccount.value,
    });
    return resultsToOptions(campaigns.results, 'name') || [];
  };

  const loadEmailConfigs = async () => {
    await props.getAccountEmailConfigs(props.selectedAccount.value);
  };

  useEffect(() => {
    if (props.selectedAccount) {
      loadEmailConfigs();
    }
    props.change('campaigns', []);
    props.change('emails', []);
  }, [props.selectedAccount]);

  const emailOptions = resultsToOptions(props.emailConfigs.results, 'email') || [];

  const lastRunDate = useMemo(() => {
    const latest = first(props.timelines.results);
    return latest ? first(first(latest.sequences).steps).modified_date : '';
  }, [props.timelines.results]);

  return (
    <div className={classes.filterContainer}>
      <form className="instance-form__body flex justify-content-between ml-0" onSubmit={handleSubmit}>
        <Grid container spacing={1}>
          <Grid item>
            {/* eslint-disable jsx-a11y/label-has-for */}
            <label className="required">Account</label>
            <Field
              name="account"
              validate={[requiredOptionValidator]}
              component={RenderSelectInput}
              getOptions={loadAccounts}
              className={classes.inpWidth}
            />
          </Grid>
          {props.selectedAccount &&
            <>
              <Grid item>
                {/* eslint-disable jsx-a11y/label-has-for */}
                <label className="required">Launch date</label>
                <Field
                  name="launchDate"
                  component={propCus => <div><DateTimeWithRangeFilter {...propCus.input} /></div>}
                />
              </Grid>
              <Grid item>
                {/* eslint-disable jsx-a11y/label-has-for */}
                <label>Campaign Name (optional)</label>
                <Field
                  key={`${props.selectedAccount.value}-campaigns`}
                  name="campaigns"
                  component={RenderSelectInput}
                  getOptions={loadCampaigns}
                  className={classes.inpWidth}
                  isMulti
                />
              </Grid>
              <Grid item md={3}>
                {/* eslint-disable jsx-a11y/label-has-for */}
                <label>Sending Email (optional)</label>
                <Field
                  name="emails"
                  component={propsCus => (
                    <Select
                      isMulti
                      isLoading={props.emailConfigs.loading}
                      placeholder="Select..."
                      options={emailOptions || []}
                      {...propsCus.input}
                    />
                  )}
                  className={classes.inpWidth}
                />
              </Grid>
            </>
          }
        </Grid>
        <Grid item>
          <div className="flex">
            {lastRunDate &&
              <div className={classes.lastRunInfo}>
                Last Run <br />{moment(lastRunDate).local().format('MMM D, YYYY h:mmA')}
              </div>
            }
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="large"
              className={classes.submitBtn}
              disabled={invalid || submitting}
            >
              Generate Report
            </Button>
          </div>
        </Grid>
      </form>
    </div>
  );
};

TimelineFilter.propTypes = {
  classes: PropType.shape({}).isRequired,
  handleSubmit: PropType.func.isRequired,
  invalid: PropType.bool.isRequired,
  submitting: PropType.bool.isRequired,
  accounts: PropType.shape({
    loading: PropType.bool,
    results: PropType.arrayOf(PropType.shape({})),
  }).isRequired,
  emails: PropType.shape({
    loading: PropType.bool,
    results: PropType.arrayOf(PropType.shape({})),
  }).isRequired,
  getAccounts: PropType.func.isRequired,
  getCampaigns: PropType.func.isRequired,
  getAccountEmailConfigs: PropType.func.isRequired,
};

const styles = () => ({
  filterContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'end',
    marginBottom: '2rem',
  },
  submitBtn: {
    marginTop: '2.375rem',
  },
  lastRunInfo: {
    marginTop: '2.375rem',
    fontSize: '0.75rem',
    fontWeight: '400',
    lineHeight: '1rem',
    color: timlineRunInfo,
    width: '6.5rem',
    marginRight: '1rem',
  },
  inpWidth: {
    width: '10rem',
  },
});

const formName = 'TimlineFilterForm';
const selector = formValueSelector(formName);

export default compose(
  withStyles(styles),
  connect(state => ({
    timelines: state.timelines,
    accounts: state.accounts,
    emailConfigs: state.accountEmailConfigs,
    selectedAccount: selector(state, 'account'),
    initialValues: {
      launchDate: {
        dateSelector: LAST_MONTH_DATE_OPTION,
        startDate: moment().subtract(30, 'd').local(),
        endDate: moment().local(),
      },
    },
  }), {
    getAccounts,
    getCampaigns,
    getAccountEmailConfigs,
  }),
  reduxForm({
    form: formName,
  }),
)(TimelineFilter);
